import { useState, useMemo } from "react";
import { GitBranch, FileText, FilePlus, FileX, FileEdit, Check, ChevronDown, ChevronRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DiffViewerWidget, type DiffFile } from "./DiffViewerWidget";
import { cn } from "@/lib/utils";

// Same gradient pool as AI chat bubbles (purple)
const BUBBLE_GRADIENTS = [
  "linear-gradient(135deg, rgba(125,92,240,0.09) 0%, rgba(110,78,220,0.04) 100%)",
  "linear-gradient(150deg, rgba(130,95,245,0.08) 0%, rgba(115,82,228,0.04) 100%)",
  "linear-gradient(120deg, rgba(120,88,235,0.09) 0%, rgba(128,95,240,0.04) 100%)",
  "linear-gradient(140deg, rgba(128,94,242,0.08) 0%, rgba(105,75,218,0.04) 100%)",
  "linear-gradient(160deg, rgba(160,105,255,0.14) 0%, rgba(125,75,235,0.09) 100%)",
];

function pickGradient(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = ((hash << 5) - hash + seed.charCodeAt(i)) | 0;
  return BUBBLE_GRADIENTS[Math.abs(hash) % BUBBLE_GRADIENTS.length]!;
}

interface GitChange {
  status: string;
  path: string;
}

interface GitStatusData {
  branch: string;
  clean: boolean;
  changes: GitChange[];
  ahead: number;
  behind: number;
  diff_files?: DiffFile[];
  diff_total_additions?: number;
  diff_total_deletions?: number;
}

interface GitStatusWidgetProps {
  data: GitStatusData;
  actions?: Array<{ id: string; label: string; style?: string }>;
  onAction?: (actionId: string) => void;
}

const statusIcon: Record<string, typeof FileText> = {
  M:  FileEdit,
  A:  FilePlus,
  D:  FileX,
  "?": FilePlus,
  "??": FilePlus,
};

const statusColor: Record<string, string> = {
  M:  "text-daydra-warning",
  A:  "text-daydra-success",
  D:  "text-daydra-error",
  "?": "text-daydra-dim",
  "??": "text-daydra-dim",
};

const statusLabel: Record<string, string> = {
  M:  "modified",
  A:  "added",
  D:  "deleted",
  "?": "untracked",
  "??": "untracked",
};

export function GitStatusWidget({ data, actions, onAction }: GitStatusWidgetProps) {
  const { branch = "unknown", clean = true, changes = [], ahead = 0, behind = 0 } = data ?? {};
  const hasDiff = data?.diff_files && data.diff_files.length > 0;
  const [showDiff, setShowDiff] = useState(false);
  const [showStaged, setShowStaged] = useState(true);
  const [showUnstaged, setShowUnstaged] = useState(true);
  const [showUntracked, setShowUntracked] = useState(true);

  const gradient = useMemo(
    () => pickGradient(branch ?? "git-status"),
    [branch],
  );

  // Group changes by status
  const stagedChanges = changes.filter(c => c.status === "A" || c.status === "M" || c.status === "D");
  const unstagedChanges = changes.filter(c => c.status.includes("M") && c.status !== "A" && c.status !== "D");
  const untrackedChanges = changes.filter(c => c.status === "?" || c.status === "??");

  return (
    <div
      className="animate-slide-up rounded-3xl rounded-bl-lg overflow-hidden max-w-2xl"
      style={{ background: gradient }}
    >
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="flex items-center justify-center h-6 w-6 rounded-lg bg-white/10">
          <GitBranch size={14} className="text-daydra-accent" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2">
            <span className="text-sm font-semibold text-daydra-text">{branch}</span>
            {ahead > 0 && (
              <Badge variant="accent" size="sm">{ahead} ahead</Badge>
            )}
            {behind > 0 && (
              <Badge variant="warning" size="sm">{behind} behind</Badge>
            )}
          </div>
          <p className="text-xs text-daydra-dim mt-0.5">
            {clean 
              ? "Working tree clean" 
              : `${changes.length} change${changes.length !== 1 ? "s" : ""}`
            }
          </p>
        </div>
        {clean && (
          <Check size={16} className="text-daydra-success shrink-0" />
        )}
      </div>

      {/* Collapsible sections */}
      {stagedChanges.length > 0 && (
        <>
          <button
            onClick={() => setShowStaged(!showStaged)}
            className="flex items-center justify-between w-full px-4 py-2 text-xs text-daydra-muted hover:text-daydra-text border-t border-white/[0.06] transition-colors"
          >
            <div className="flex items-center gap-1.5">
              {showStaged ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              <span className="font-medium">Staged ({stagedChanges.length})</span>
            </div>
          </button>
          {showStaged && (
            <div className="px-4 pb-2">
              {stagedChanges.slice(0, 8).map((change, i) => {
                const Icon = statusIcon[change.status] ?? FileText;
                const color = statusColor[change.status] ?? "text-daydra-dim";
                return (
                  <div key={i} className="flex items-center gap-2 py-1">
                    <Icon size={12} className={cn("shrink-0", color)} />
                    <span className="flex-1 font-mono text-2xs text-daydra-text truncate">
                      {change.path}
                    </span>
                  </div>
                );
              })}
              {stagedChanges.length > 8 && (
                <div className="text-2xs text-daydra-muted text-center py-1">
                  +{stagedChanges.length - 8} more
                </div>
              )}
            </div>
          )}
        </>
      )}

      {unstagedChanges.length > 0 && (
        <>
          <button
            onClick={() => setShowUnstaged(!showUnstaged)}
            className="flex items-center justify-between w-full px-4 py-2 text-xs text-daydra-muted hover:text-daydra-text border-t border-white/[0.06] transition-colors"
          >
            <div className="flex items-center gap-1.5">
              {showUnstaged ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              <span className="font-medium">Unstaged ({unstagedChanges.length})</span>
            </div>
          </button>
          {showUnstaged && (
            <div className="px-4 pb-2">
              {unstagedChanges.slice(0, 8).map((change, i) => {
                const Icon = statusIcon[change.status] ?? FileText;
                const color = statusColor[change.status] ?? "text-daydra-dim";
                return (
                  <div key={i} className="flex items-center gap-2 py-1">
                    <Icon size={12} className={cn("shrink-0", color)} />
                    <span className="flex-1 font-mono text-2xs text-daydra-text truncate">
                      {change.path}
                    </span>
                  </div>
                );
              })}
              {unstagedChanges.length > 8 && (
                <div className="text-2xs text-daydra-muted text-center py-1">
                  +{unstagedChanges.length - 8} more
                </div>
              )}
            </div>
          )}
        </>
      )}

      {untrackedChanges.length > 0 && (
        <>
          <button
            onClick={() => setShowUntracked(!showUntracked)}
            className="flex items-center justify-between w-full px-4 py-2 text-xs text-daydra-muted hover:text-daydra-text border-t border-white/[0.06] transition-colors"
          >
            <div className="flex items-center gap-1.5">
              {showUntracked ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              <span className="font-medium">Untracked ({untrackedChanges.length})</span>
            </div>
          </button>
          {showUntracked && (
            <div className="px-4 pb-2">
              {untrackedChanges.slice(0, 8).map((change, i) => {
                const Icon = statusIcon[change.status] ?? FileText;
                const color = statusColor[change.status] ?? "text-daydra-dim";
                return (
                  <div key={i} className="flex items-center gap-2 py-1">
                    <Icon size={12} className={cn("shrink-0", color)} />
                    <span className="flex-1 font-mono text-2xs text-daydra-text truncate">
                      {change.path}
                    </span>
                  </div>
                );
              })}
              {untrackedChanges.length > 8 && (
                <div className="text-2xs text-daydra-muted text-center py-1">
                  +{untrackedChanges.length - 8} more
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Inline diff toggle */}
      {hasDiff && (
        <>
          <button
            onClick={() => setShowDiff((v) => !v)}
            className="flex items-center gap-1.5 w-full px-4 py-2 text-2xs text-daydra-muted hover:text-daydra-text border-t border-white/[0.06] transition-colors"
          >
            {showDiff ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            <span>{showDiff ? "Hide diff" : "Show diff"}</span>
            <span className="ml-auto text-daydra-success/70">+{data.diff_total_additions ?? 0}</span>
            <span className="text-daydra-error/70">-{data.diff_total_deletions ?? 0}</span>
          </button>
          {showDiff && (
            <DiffViewerWidget
              data={{
                files: data.diff_files!,
                total_files: data.diff_files!.length,
                total_additions: data.diff_total_additions ?? 0,
                total_deletions: data.diff_total_deletions ?? 0,
              }}
              embedded
            />
          )}
        </>
      )}

      {/* Actions */}
      {actions && actions.length > 0 && (
        <div className="flex gap-2 px-4 py-3 border-t border-white/[0.06]">
          {actions.map((action) => (
            <Button
              key={action.id}
              variant={action.style === "primary" ? "default" : "secondary"}
              size="sm"
              onClick={() => onAction?.(action.id)}
            >
              {action.label}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}
